import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import HeaderTitleContent from '../../../common/components/atom/HeaderTitleContent/HeaderTitleContent'
import TagCellEditor from '../../../common/components/molecule/TagCellEditor/TagCellEditor'
import styles from './LuminaireDashboardPage.module.scss'
import { LuminaireMetricFilter } from '../../../features/luminaire/types/luminaire.types'
import FilterV2 from '../../../features/filter/components/FilterV2'
import { useDefaultFilter } from '../../../common/hooks/useDefaultFilter'
import { useLuminaireByDevEui, useKnownLuminaireTags } from '../../../features/luminaire/hooks/useLuminaires'
import useUpdateLuminaireTags from '../../../features/luminaire/hooks/useUpdateLuminaireTags'
import { PermissionTooltip, useCapability } from '../../../features/rbac'
import * as LuminaireActivePowerChart from '../../../features/luminaire/components/LuminaireActivePowerChart'
import * as LuminairePowerFactorChart from '../../../features/luminaire/components/LuminairePowerFactorChart'
import LuminaireConsumptionChartPresenter from '../../../features/luminaire/components/LuminaireConsumptionChart/LuminaireConsumptionChartPresenter'
import * as LuminaireDimmingLevelChart from '../../../features/luminaire/components/LuminaireDimmingLevelChart'
import * as LuminaireDownlinkRssiChart from '../../../features/luminaire/components/LuminaireDownlinkRssiChart'
import LuminaireActiveAlertsCardPresenter from '../../../features/luminaire/components/LuminaireActiveAlertsCard/LuminaireActiveAlertsCardPresenter'
import LuminaireLastValuesMetrics from './LuminaireLastValuesMetrics'
import LuminaireEffectiveConfigSection from '../../../features/luminaire/components/LuminaireEffectiveConfigSection/LuminaireEffectiveConfigSection'
import { useDeviceCatalog } from '../../../features/commandBus'
import { DeviceNotProvisionedError } from '../../../features/commandBus/service/commandBusAPI'
import ActionPresenter from '../../../common/components/molecule/Action/ActionPresenter'

export default function LuminaireDashboardPage() {
  const { devEui } = useParams<{ devEui: string }>()
  const navigate = useNavigate()
  const { filter: globalFilter } = useDefaultFilter(7, '1h')
  const [selectedChart, setSelectedChart] = useState<{ component: React.ReactNode; title: string } | null>(null)
  const { luminaire } = useLuminaireByDevEui(devEui)
  const updateTagsMutation = useUpdateLuminaireTags()
  const canEditTags = useCapability('equipment.edit')
  const knownTags = useKnownLuminaireTags()
  // Catalogue command-bus : sert à 1) afficher le badge MoMi à côté du titre,
  // 2) décider si on doit masquer toute la section commandes (device non
  //    provisionné dans le bus → on n'affiche rien plutôt qu'un panel vide).
  const catalogQuery = useDeviceCatalog(devEui)
  const isDeviceProvisioned = !(catalogQuery.error instanceof DeviceNotProvisionedError)

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/main', { replace: false })
    }
  }

  const handleChartClick = (component: React.ReactNode, title: string) => {
    setSelectedChart({ component, title })
  }

  const closeModal = () => {
    setSelectedChart(null)
  }

  const filter: LuminaireMetricFilter = {
    devEui: devEui ?? '',
    startDate: globalFilter.startDate ?? '',
    stopDate: globalFilter.stopDate ?? '',
    aggregateWindow: globalFilter.aggregateWindow ?? '1h',
  }

  const eui = devEui ?? ''

  return (
    <div className={styles.luminairePage}>
      <HeaderTitleContent
        withGoBackBtn={true}
        goBackBtnLabel="Retour"
        onGoBack={handleGoBack}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span>Luminaire {devEui}</span>
          </div>
          {isDeviceProvisioned && eui && (
            <ActionPresenter
              deviceId={eui}
              buttonSize="small"
              buttonVariant="secondary"
              buttonLabel="Actions"
            />
          )}
        </div>
      </HeaderTitleContent>

      <div className={styles.mainContent}>
        <div className={styles.leftContent}>
          {luminaire && (
            <div className={styles.tagsSection}>
              <span className={styles.tagsLabel}>Tags :</span>
              <PermissionTooltip allowed={canEditTags}>
                <TagCellEditor
                  tags={luminaire.lum_tags ?? []}
                  onSave={(next) =>
                    updateTagsMutation.mutateAsync({ luminaire, tags: next })
                  }
                  compact={false}
                  disabled={!canEditTags}
                  suggestions={knownTags}
                />
              </PermissionTooltip>
            </div>
          )}

          <div className={styles.filterSection}>
            <FilterV2 />
          </div>

          <LuminaireLastValuesMetrics devEui={eui} filter={filter} />

          <LuminaireEffectiveConfigSection devEui={eui} />

          <div className={styles.chartsGrid}>
            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminaireConsumptionChartPresenter devEui={eui} filter={filter} />,
                  "Évolution de la Consommation d'Énergie"
                )
              }
            >
              <LuminaireConsumptionChartPresenter devEui={eui} filter={filter} />
            </div>

            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminaireActivePowerChart.Chart devEui={eui} filter={filter} />,
                  'Puissance active'
                )
              }
            >
              <LuminaireActivePowerChart.Chart devEui={eui} filter={filter} />
            </div>

            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminairePowerFactorChart.Chart devEui={eui} filter={filter} />,
                  'Facteur de puissance'
                )
              }
            >
              <LuminairePowerFactorChart.Chart devEui={eui} filter={filter} />
            </div>

            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminaireDimmingLevelChart.Chart devEui={eui} filter={filter} />,
                  'Dimming level'
                )
              }
            >
              <LuminaireDimmingLevelChart.Chart devEui={eui} filter={filter} />
            </div>

            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminaireDownlinkRssiChart.Chart devEui={eui} filter={filter} />,
                  'RSSI downlink'
                )
              }
            >
              <LuminaireDownlinkRssiChart.Chart devEui={eui} filter={filter} />
            </div>

            <div
              className={styles.chartWrapper}
              onClick={() =>
                handleChartClick(
                  <LuminaireActiveAlertsCardPresenter devEui={eui} filter={filter} />,
                  'Alertes actives'
                )
              }
            >
              <LuminaireActiveAlertsCardPresenter devEui={eui} filter={filter} />
            </div>
          </div>

        </div>
      </div>

      {/* Click-to-zoom modal — mirrors ClientV2Page pattern */}
      {selectedChart && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalHeaderLeft}>
                <span className={styles.modalTitle}>{selectedChart.title}</span>
              </div>
              <div className={styles.modalHeaderRight}>
                <button className={styles.closeButton} onClick={closeModal}>
                  ×
                </button>
              </div>
            </div>
            <div className={styles.modalBody}>{selectedChart.component}</div>
          </div>
        </div>
      )}
    </div>
  )
}
